<template>
  <nav>
    <v-overlay
      data-testid="overlay-screen"
      :value="overlay"
      :absolute="absolute"
      :opacity="opacity"
      :dark="false"
      class="my-overlay text-center"
      color="background"
    >
      <v-tooltip
        bottom
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            data-testid="overlay-signout"
            class="exit-btn mx-md-3  greytext--text"
            absolute
            top
            right
            elevation="0"
            v-bind="attrs"
            @click="signOut"
            v-on="on"
          >
            {{ $t('signOut') }}
            <v-icon class="pl-3">
              mdi-exit-to-app
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t('signOut') }}</span>
      </v-tooltip>

      <v-card-title class="text-h5 font-weight-bold text-center justify-center mt-16 mb-4 no-break">
        {{ $t('verificationWaitingTitle') }}
      </v-card-title>
      <v-card-subtitle class=" text-center justify-center mb-16 darktext--text text-subtitle-1 no-break">
        {{ $t('verificationWaitingSubTitle') }}
      </v-card-subtitle>

      <v-spacer />
      <v-progress-circular
        :size="125"
        :width="7"
        indeterminate
        class="my-16"
        color="accent"
      />
      <v-spacer />

      <v-card-text class="mt-16 body-2 greytext--text ">
        {{ $t('didntGetEmail') }}    <br>
        {{ $t('checkSpamFolderOr') }}  
        <router-link
          data-testid="overlay-resend-link"
          to="#"
          @click.native="resendConfirmationLink"
        >
          {{ $t('sendAgain') }}
        </router-link>
        <v-progress-circular
          v-if="isLoading"
          size="20"
          indeterminate
          class="mx-2"
        />
      </v-card-text>
    </v-overlay>

    <v-row
      class="fill-height"
      align="center"
    >
      <v-col
        cols="12"
        sm="8"
        md="6"
        offset-sm="2"
        offset-md="3"
        align="center"
      >
        <v-card
          max-width="600"
          elevation="0"
          rounded="xxl"
          color="background"
        >
          <LanguageSwitcher @languageSelected = "updateLocale"/>
          <v-card-title
            :class="mobile() ? 'text-h5 font-weight-bold text-center justify-center no-break' : 'mx-10  text-h5 font-weight-bold text-center justify-center no-break'"
          >
            {{ $t('createAccountTitle') }}
          </v-card-title>
          <v-card-actions class="px-6" v-if="showGoogleLogin || space == 'id'">
            <GoogleButton data-testid="google-button"/>
          </v-card-actions>
          <v-row align="center" no-gutters class="ma-8" v-if="showGoogleLogin || space == 'id'">              
            <v-divider class="text-divider"></v-divider><span class="px-3 greytext--text">{{ $t('or')}}</span><v-divider class="text-divider"></v-divider>
          </v-row>
          <v-card-text>
            <v-alert
              v-if="alertType == 'success'"
              type="success"
              class="rounded-xxl text-left"
            >
              {{ $t(alertMsg) }}<br />{{ $t('didNotGetMailcheckSpam') }}         
              <router-link
                data-testid="resend-link"
                to="#"
                class="white--text"
                @click.native="resendConfirmationLink"
              >
                {{ $t('resendMessage') }}
              </router-link>
              <v-progress-circular
                v-if="isLoading"
                size="20"
                indeterminate
                class="mx-2"
              />
            </v-alert>
            <v-alert
              v-if="alertType == 'error'"
              type="error"
              class="rounded-xxl text-left"
            >
              {{ $t(alertMsg) }} 
            </v-alert>
                
            <v-form
              ref="form"
              v-model="valid"
              @keyup.enter="register"
              @submit.prevent
              class="checkbox-align-start"
            >
              <v-text-field
                v-model="firstName"
                data-testid="first-name-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :placeholder="$t('createAccountFirstNamePlaceholder')"
                :rules="firstNameRules"
                @keyup.enter="focusNext($event.target)"
              />
              <v-text-field
                v-model="lastName"
                data-testid="last-name-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :placeholder="$t('createAccountLastNamePlaceholder')"
                :rules="lastNameRules"
                @keyup.enter="focusNext($event.target)"
              />
              <br>
              <v-text-field
                v-model="email"
                data-testid="email-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :placeholder="$t('createAccountEmailPlaceholder')"
                :rules="emailRules"
                @keyup.enter="focusNext($event.target)"
              />
              <v-text-field
                v-model="confirmEmail"
                data-testid="email-confirm-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :placeholder="$t('createAccountConfirmEmailPlaceholder')"
                :rules="emailRules.concat(emailConfirmationRule)"
                @keyup.enter="focusNext($event.target)"
              />
              <br>
              <v-text-field
                v-model="password"
                data-testid="password-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('createAccountPasswordPlaceholder')"
                :rules="passwordRules"
                @click:append="showPassword = !showPassword"
              />
              <v-text-field
                v-model="confirmPassword"
                data-testid="password-confirm-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :append-icon="showPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPasswordConfirm ? 'text' : 'password'"
                :placeholder="$t('createAccountConfirmPasswordPlaceholder')"
                :rules="passwordRules.concat(passwordConfirmationRule)"
                @click:append="showPasswordConfirm = !showPasswordConfirm"
              />
              <v-checkbox
                v-model="acceptedGdpr"
                data-testid="gdpr-input"
                color="accent"
                @change="gdpr($event)"
              >
                <template v-slot:label>
                  <!-- eslint-disable vue/no-v-html -->
                  <div
                    ref="link"
                    v-html="$t('checkboxGdprLabel', {
                      'terms-of-services': `<a target='_blank' href='/terms-of-service'>${ $t('termsOfServices') }</a>`,
                      'privacy-policy': `<a target='_blank' href='/privacy-policy'>${ $t('privacyPolicy') }</a>`
                    })" 
                  />
                </template>
              </v-checkbox>
              <v-card-actions class="px-0">
                <v-btn
                  data-testid="submit-button"
                  type="submit"
                  :disabled="buttonDisabled"
                  color="accent"
                  large
                  block
                  class="rounded-xxl text-none"
                  @click="register"
                >
                  {{ $t('signUp') }}
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card-text>
                    
          <router-link
            v-if="referer != null"
            :to="'/login?referer='+ referer"
            class="text-decoration-none text-body-2 darktext--text"
          >
            {{ $t('createAccountLoginWithExistingAccount') }}
          </router-link>
          <router-link
            v-else
            to="/login"
            class="text-decoration-none text-body-2 darktext--text"
          >
            {{ $t('createAccountLoginWithExistingAccount') }}
          </router-link>
        </v-card>
      </v-col>
    </v-row>
  </nav>
</template>
<style scoped>
  .my-overlay :deep() .v-overlay__content {
    width: 100%;
    height: 100%;
  }
  .exit-btn{
    text-transform: none;
    font-weight: normal;
    font-size: 16px;
  }
</style>
<script>
import { ruleRequired, emailRuleValid, ruleValidMin2Max70Characters, passwordRuleValid, passwordRuleCharactersValid } from '../../rules/formRules.js';
import i18n from '../../plugins/i18n'
import { registerUser, resendEmail } from '../../api/userApi'
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin';
import LanguageSwitcher from '../../components/LanguageSwitcher'
import {loginAuth, logoutAuth } from '../../auth/auth.js'
import Echo from "laravel-echo"
import GoogleButton from '../../components/GoogleButton'

window.Pusher = require('pusher-js')
export default {
    components: {
        LanguageSwitcher,
        GoogleButton
    },
    mixins: [repeatedMethodsMixin],
    data(){
        return{
            valid: true,
            email: '',
            confirmEmail: '',
            firstName: '',
            lastName: '',
            password: '',
            confirmPassword: '',
            acceptedGdpr: undefined,
            showPassword: false,
            showPasswordConfirm: false,
            alertType: '',
            alertMsg: '',
            buttonDisabled: true,
            token: '',
            isLoading: false,
            overlay: false,
            absolute: true,
            opacity: 1,
            userId: '',
            userObj: {},
            referer: null,
            language: window.$cookies.get('language') ? window.$cookies.get('language') : 'en',
            focusNext(elem) {
                const currentIndex = Array.from(elem.form.elements).indexOf(elem);
                elem.form.elements.item(
                    currentIndex < elem.form.elements.length - 1 ?
                    currentIndex + 1 :
                    0
                ).focus();
            },
            languageRuleActive: true,
            showGoogleLogin: localStorage.space? JSON.parse(localStorage.space).show_google_login: false,
            space: window.location.host.split('.')[0]
         }
    },
    computed: {
        firstNameRules() {
            return this.languageRuleActive ? ruleRequired('labelFirstName').concat(ruleValidMin2Max70Characters('labelFirstName')) : []
        },
        lastNameRules() {
            return this.languageRuleActive ? ruleRequired('labelLastName').concat(ruleValidMin2Max70Characters('labelLastName')) : []
        },
        emailRules() {
            return this.languageRuleActive ? ruleRequired('labelEmail').concat(emailRuleValid) : []
        },
        passwordRules() {
            return this.languageRuleActive ? ruleRequired('labelPassword').concat(passwordRuleCharactersValid, passwordRuleValid) : []
        },
        passwordConfirmationRule() {
            return () =>
                this.password === this.confirmPassword || i18n.t('fieldErrorMatch', {'field': i18n.t('labelPassword') })
        },
        emailConfirmationRule() {
            return () =>
                this.email === this.confirmEmail || i18n.t('fieldErrorMatch', {'field': i18n.t('labelEmail') })
        }
    },
    watch: {
      '$i18n.locale': function() {
        setTimeout(() => {
            this.$refs['link'].children[0].addEventListener('click', function(event) {
                event.stopImmediatePropagation()
            })
            this.$refs['link'].children[1].addEventListener('click', function(event) {
                event.stopImmediatePropagation()
            })
          }, 100)
      }
    },
    created() {
        let queryString = window.location.search;
        let urlParams = new URLSearchParams(queryString);
        let referer = urlParams.get('referer')
        if (referer) {
            this.referer = referer;
        }
    },
    mounted() {
        this.$refs['link'].children[0].addEventListener('click', function(event) {
            event.stopImmediatePropagation()
        })
        this.$refs['link'].children[1].addEventListener('click', function(event) {
            event.stopImmediatePropagation()
        })
    },
    methods: {
        gdpr: function(e) {
            this.buttonDisabled = e === true ? false : true
        },
        async register(){
            this.language = window.$cookies.get('language') ? window.$cookies.get('language') : 'en'
            this.$refs.form.validate()
            if(this.valid){
                this.buttonDisabled = true
                try {
                    let data = null;
                    if (this.$route.query.referer) {
                        data = {
                            data: {
                                type: "users", 
                                attributes: {
                                    first_name: this.firstName.trim(),
                                    last_name: this.lastName.trim(),
                                    email: this.email.trim(),
                                    email_confirmation: this.confirmEmail.trim(),
                                    password: this.password.trim(),
                                    password_confirmation: this.confirmPassword.trim(),
                                    accepted_gdpr: this.acceptedGdpr,
                                    language: this.language.trim()
                                }
                            },
                            meta: {
                                'referer': this.$route.query.referer,
                            }
                        };
                    } else {
                        data = {
                            data: {
                                type: "users",
                                attributes: {
                                    first_name: this.firstName.trim(),
                                    last_name: this.lastName.trim(),
                                    email: this.email.trim(),
                                    email_confirmation: this.confirmEmail.trim(),
                                    password: this.password.trim(),
                                    password_confirmation: this.confirmPassword.trim(),
                                    accepted_gdpr: this.acceptedGdpr,
                                    language: this.language.trim()
                                }
                            }
                        };
                    }

                    const response = await registerUser(data)
                    if(response.status == 201) {
                        this.overlay = true;
                        this.alertType = 'success'
                        this.alertMsg =  'createAccountSuccess'
                        this.token = response.data.meta.access_token;
                        this.userId = response.data.data.id;
                        this.userObj = response.data.data;
                        this.listen();
                    }
                    this.$refs.form.reset()
                } catch (e) {
                    if(e.response){
                        this.alertType = 'error'
                        if(e.response.status == 460){
                            this.alertMsg =  'createAccountErrorEmailAlreadyUsed'
                        }else if (e.response.status == 422 ){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'The password must contain at least one special character!'){
                                    this.alertMsg = i18n.t('resetPasswordInvalidCharacter')
                                }else{
                                    this.alertMsg =  i18n.t('serverError')
                                }
                            }
                        }
                        else {
                            this.alertMsg = i18n.t('serverError')
                        }
                    }
                }
                this.buttonDisabled = this.acceptedGdpr === true ? false : true
            }
        },
        async resendConfirmationLink(){
            this.language = window.$cookies.get('language') ? window.$cookies.get('language') : 'en'
            this.isLoading = true
            try {
                const response = await resendEmail(this.token)
                if(response.status == 200) {
                    this.alertType = 'success'
                    this.alertMsg =  'verificationLinkSent'
                    this.isLoading = false
                }
            } catch (e){
                this.alertType = 'error'
                this.alertMsg =  'serverError'
                this.isLoading = false
            }
        },
        async listen(){
          if (this.token) {
              window.Echo = new Echo({
                  broadcaster: 'pusher',
                  key: process.env.VUE_APP_PUSHER_KEY,
                  cluster: 'eu',
                  encryted: true,
                  authEndpoint: process.env.VUE_APP_API_BASE_URL + "/v1/broadcasting/auth",
                  auth: {
                      headers: {
                          'Accept': "application/vnd.api+json",
                          'Authorization': 'Bearer ' + this.token
                      }
                  },
              });
              window.Echo.private('user-verification-tracker.' + this.userId)
              .listen('UserVerified', () => {
                  this.hideLoader();
              });
          }
        },
        hideLoader() {
          loginAuth(this.token, this.userObj)
          i18n.locale = this.userObj.attributes.language && this.userObj.attributes.language.trim()
          window.$cookies.set("language", i18n.locale, 0, null, process.env.VUE_APP_DOMAIN)
          const space = window.location.host.split('.')[0];

          if (this.$route.query.referer) {
                window.location.href = process.env.VUE_APP_PROTOCOL + "://" + this.$route.query.referer;
          } else {
              if (space == 'id') {
                this.$router.push('/spaces')
              } else {
                window.location.href = process.env.VUE_APP_PROTOCOL + "://id." + process.env.VUE_APP_DOMAIN + "/spaces"
              }
          }
          
        },
        signOut(){
          logoutAuth()
          this.$router.push({path: '/login'})
        },
        updateLocale() {
            this.languageRuleActive = false
            this.$nextTick(() => {
              this.languageRuleActive = true
            })
        },
    }
    }
</script>